import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../../services/supabaseClient';
import { AdminService } from '../../services/adminService';
import { ENV } from '../../config/env';
import {
  resolveMessageDisplayContent,
  resolveReplyPreview,
  resolveTemplateButtons,
} from '../../utils/whatsappTemplateContent';
import { TemplateBubbleButtons } from '../../components/admin/WhatsAppTemplatePreview';
import {
  Search, Send, RefreshCw, UserCheck, ChevronLeft,
  Volume2, VolumeX, MessageSquare, Users, ArrowDownCircle
} from 'lucide-react';

const maskPhone = (p) => p ? `****${p.slice(-4)}` : '';
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const y = new Date(); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export default function WhatsApp({
  embedded = false,
  automationAccountIds = null,
  automationAccounts = [],
  defaultAccountId = null,
}) {
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [userById, setUserById] = useState({});
  const [telecallers, setTelecallers] = useState([]);
  const [reassignUser, setReassignUser] = useState('');
  const [showReassign, setShowReassign] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [sendAccountId, setSendAccountId] = useState(defaultAccountId || '');
  const messagesEndRef = useRef(null);

  const automationOnly = Array.isArray(automationAccountIds) && automationAccountIds.length > 0;

  const effectiveAccountIds = useMemo(() => {
    if (!automationOnly) return null;
    if (automationAccounts.length <= 1) return automationAccountIds;
    const picked = sendAccountId || defaultAccountId;
    return picked ? [picked] : automationAccountIds;
  }, [automationOnly, automationAccountIds, automationAccounts.length, sendAccountId, defaultAccountId]);

  const effectiveIdsKey = effectiveAccountIds?.join(',') || '';

  useEffect(() => {
    if (defaultAccountId) setSendAccountId(defaultAccountId);
  }, [defaultAccountId]);

  const hydrateAssignees = useCallback(async (contactList) => {
    const ids = [...new Set((contactList || []).map((c) => c.assigned_to).filter(Boolean))];
    if (!ids.length) return;
    const { data } = await supabase.from('users').select('id, name, email, phone').in('id', ids);
    if (!data?.length) return;
    setUserById((prev) => {
      const next = { ...prev };
      data.forEach((u) => { next[String(u.id)] = u; });
      return next;
    });
  }, []);

  const loadContacts = useCallback(async () => {
    let nextContacts = [];
    if (automationOnly && effectiveAccountIds?.length) {
      const ids = effectiveAccountIds;
      const [{ data: byAccount }, { data: msgRows }] = await Promise.all([
        supabase
          .from('whatsapp_contacts')
          .select('*')
          .in('account_id', ids)
          .order('last_message_at', { ascending: false }),
        supabase
          .from('whatsapp_messages')
          .select('contact_number')
          .in('account_id', ids)
          .order('created_at', { ascending: false })
          .limit(5000),
      ]);
      const phones = [...new Set((msgRows || []).map((m) => m.contact_number).filter(Boolean))];
      let byPhone = [];
      if (phones.length) {
        const { data } = await supabase
          .from('whatsapp_contacts')
          .select('*')
          .in('phone', phones)
          .order('last_message_at', { ascending: false });
        byPhone = data || [];
      }
      const merged = new Map();
      [...(byAccount || []), ...byPhone].forEach((c) => merged.set(c.id, c));
      nextContacts = [...merged.values()].sort(
        (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0),
      );
    } else {
      const { data } = await supabase
        .from('whatsapp_contacts')
        .select('*')
        .order('last_message_at', { ascending: false });
      nextContacts = data || [];
    }
    setContacts(nextContacts);
    await hydrateAssignees(nextContacts);
    setLoading(false);
  }, [automationOnly, effectiveIdsKey, effectiveAccountIds, hydrateAssignees]);

  useEffect(() => {
    if (automationOnly) setSelectedContact(null);
  }, [effectiveIdsKey, automationOnly]);

  const loadTelecallers = useCallback(async () => {
    try {
      const tcs = await AdminService.getTelecallers({ status: 'active' });
      setTelecallers(tcs);
      setUserById((prev) => {
        const next = { ...prev };
        tcs.forEach((u) => { next[String(u.id)] = u; });
        return next;
      });
    } catch (e) {
      console.error('[WhatsApp] loadTelecallers:', e);
    }
  }, []);

  useEffect(() => {
    loadContacts();
    loadTelecallers();
    const interval = setInterval(loadContacts, 5000);
    const ch = supabase
      .channel('wa_admin_contacts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_contacts' }, () => loadContacts())
      .subscribe();
    return () => { clearInterval(interval); supabase.removeChannel(ch); };
  }, [loadContacts, loadTelecallers]);

  const loadMessages = useCallback(async (phone) => {
    let q = supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('contact_number', phone)
      .order('created_at', { ascending: true })
      .limit(500);
    if (automationOnly && effectiveAccountIds?.length) {
      q = q.in('account_id', effectiveAccountIds);
    }
    const { data } = await q;
    setMessages(data || []);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [automationOnly, effectiveIdsKey, effectiveAccountIds]);

  useEffect(() => {
    if (!selectedContact) return;
    loadMessages(selectedContact.phone);
    supabase.from('whatsapp_contacts').update({ unread_count: 0 }).eq('phone', selectedContact.phone);
    const ch = supabase
      .channel(`wa_admin_msgs_${selectedContact.phone}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_messages', filter: `contact_number=eq.${selectedContact.phone}` }, () => loadMessages(selectedContact.phone))
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selectedContact, loadMessages]);

  const openChat = (c) => {
    setSelectedContact(c);
    setShowReassign(false);
    setShowTemplates(false);
  };

  const sendPayloadAccount = () => {
    const accountId = sendAccountId || defaultAccountId;
    if (!accountId) return {};
    return { account_id: accountId, sender_mode: 'specific_account' };
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !selectedContact) return;
    setSending(true);
    try {
      await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: ENV.SUPABASE_ANON_KEY },
        body: JSON.stringify({
          to: selectedContact.phone, type: 'text', content: msgText.trim(),
          contact_name: selectedContact.name || null, user_id: 'admin',
          ...sendPayloadAccount(),
        }),
      });
      setMsgText('');
      setTimeout(() => loadMessages(selectedContact.phone), 500);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const handleReassign = async () => {
    if (!reassignUser || !selectedContact) return;
    await supabase.from('whatsapp_contacts').update({ assigned_to: reassignUser }).eq('id', selectedContact.id);
    await supabase.from('whatsapp_messages').update({ assigned_to: reassignUser }).eq('contact_number', selectedContact.phone);
    setSelectedContact({ ...selectedContact, assigned_to: reassignUser });
    setShowReassign(false);
    loadContacts();
  };

  const toggleMute = async () => {
    if (!selectedContact) return;
    const muted = !selectedContact.is_muted;
    await supabase.from('whatsapp_contacts').update({ is_muted: muted }).eq('id', selectedContact.id);
    setSelectedContact({ ...selectedContact, is_muted: muted });
    loadContacts();
  };

  const loadTemplates = useCallback(async (accountId) => {
    try {
      const url = accountId
        ? `${ENV.SUPABASE_URL}/functions/v1/whatsapp-templates?account_id=${accountId}`
        : `${ENV.SUPABASE_URL}/functions/v1/whatsapp-templates`;
      const res = await fetch(url, {
        headers: { apikey: ENV.SUPABASE_ANON_KEY },
      });
      const json = await res.json();
      if (json.success) {
        setTemplates((json.templates || []).filter((t) => t.status === 'APPROVED'));
      }
    } catch (e) {
      console.error('[WhatsApp] loadTemplates:', e);
    }
  }, []);

  useEffect(() => {
    loadTemplates(sendAccountId || defaultAccountId);
  }, [loadTemplates, sendAccountId, defaultAccountId]);

  const syncTemplates = async () => {
    setSyncing(true);
    await loadTemplates(sendAccountId || defaultAccountId);
    setSyncing(false);
    setShowTemplates(true);
  };

  const templatesByName = useMemo(() => {
    const map = {};
    templates.forEach((tpl) => {
      if (tpl?.name) map[tpl.name] = tpl;
    });
    return map;
  }, [templates]);

  const messageByWaId = useMemo(() => {
    const map = {};
    messages.forEach((m) => {
      if (m.wa_message_id) map[m.wa_message_id] = m;
    });
    return map;
  }, [messages]);

  const sendTemplate = async (tpl) => {
    if (!selectedContact) return;
    setSending(true);
    try {
      const bodyParamCount = Number(tpl?.param_count || 0);
      const bodyParams = [];
      for (let i = 0; i < bodyParamCount; i++) {
        const defaultValue = i === 0 ? (selectedContact?.name || '').trim() : '';
        const value = window.prompt(`Enter BODY parameter {{${i + 1}}} for template "${tpl.name}"`, defaultValue);
        if (value === null) {
          setSending(false);
          return;
        }
        bodyParams.push(String(value));
      }

      const buttons = Array.isArray(tpl?.buttons) ? tpl.buttons : [];
      const dynamicUrlButtons = buttons
        .map((b, idx) => ({ ...b, idx }))
        .filter((b) => String(b?.type || '').toUpperCase() === 'URL' && /\{\{\d+\}\}/.test(String(b?.url || '')));
      const buttonParams = [];
      dynamicUrlButtons.forEach((btn) => {
        const needed = (String(btn.url || '').match(/\{\{\d+\}\}/g) || []).length;
        for (let i = 0; i < needed; i++) {
          const value = window.prompt(
            `Enter BUTTON URL parameter for button #${btn.idx + 1} (${btn.text || 'URL'})`,
            '',
          );
          if (value === null) throw new Error('Template sending cancelled');
          buttonParams.push(String(value));
        }
      });

      const sendPayload = {
        to: selectedContact.phone, type: 'template',
        template_name: tpl.name, template_params: bodyParams, template_button_params: buttonParams,
        template_language: tpl.language || 'en_US',
        content: tpl.body_text || tpl.name,
        contact_name: selectedContact.name || null, user_id: 'admin',
        ...sendPayloadAccount(),
      };
      if (tpl.header_type) sendPayload.template_header_type = tpl.header_type;
      if (tpl.header_image_url) sendPayload.template_header_image_url = tpl.header_image_url;
      await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: ENV.SUPABASE_ANON_KEY },
        body: JSON.stringify(sendPayload),
      });
      setShowTemplates(false);
      setTimeout(() => loadMessages(selectedContact.phone), 500);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const getUserName = (uid) => {
    if (!uid) return 'Unassigned';
    const u = userById[String(uid)];
    return u ? (u.name || u.email || 'Unknown') : 'Unassigned';
  };

  const assignedUserIds = [...new Set(contacts.map(c => c.assigned_to).filter(Boolean))];
  const assignedUsers = assignedUserIds.map((id) => userById[String(id)]).filter(Boolean);

  const filtered = contacts.filter(c => {
    if (filterUser === '__unassigned__') { if (c.assigned_to) return false; }
    else if (filterUser && c.assigned_to !== filterUser) return false;
    const q = searchQ.toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
  });

  const statusIcon = (s) => {
    if (s === 'read') return <span className="text-blue-500">✓✓</span>;
    if (s === 'delivered') return <span className="text-gray-400">✓✓</span>;
    if (s === 'sent') return <span className="text-gray-400">✓</span>;
    if (s === 'failed') return <span className="text-red-500">✗</span>;
    return null;
  };

  const getAccountLabel = (id) => {
    const acc = automationAccounts.find((a) => a.id === id);
    if (!acc) return 'Automation account';
    const phone = acc.display_phone ? ` (${acc.display_phone})` : '';
    return `${acc.name}${phone}`;
  };

  const activeSendAccount = sendAccountId || defaultAccountId;
  const activeAccount = automationAccounts.find((a) => a.id === activeSendAccount);

  return (
    <div className={`${embedded ? 'h-[calc(100vh-168px)] min-h-[540px]' : 'h-[calc(100vh-80px)]'} flex bg-gray-100 rounded-xl overflow-hidden shadow-lg border border-gray-200`}>
      {/* Left: Contacts */}
      <div className={`${selectedContact ? 'hidden md:flex' : 'flex'} flex-col w-full ${embedded ? 'md:w-72' : 'md:w-96'} bg-white border-r border-gray-200`}>
        <div className="bg-emerald-700 text-white px-3 py-2">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-bold flex items-center gap-1.5 truncate min-w-0">
              <MessageSquare size={16} /> {automationOnly ? (getAccountLabel(activeSendAccount) || 'Automation Chats') : 'WhatsApp'}
            </h2>
            <button onClick={syncTemplates} disabled={syncing}
              className="flex items-center gap-1 text-[10px] bg-emerald-600 hover:bg-emerald-500 px-2 py-1 rounded-md transition shrink-0">
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
              {syncing ? '…' : 'Sync'}
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-200" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search..." className="w-full pl-8 pr-2 py-1.5 bg-emerald-600 rounded-md text-white placeholder-emerald-200 text-xs outline-none" />
          </div>
          {automationOnly && automationAccounts.length > 0 && (
            <div className="mt-1.5">
              <select
                value={activeSendAccount || ''}
                onChange={(e) => setSendAccountId(e.target.value)}
                className="w-full bg-emerald-600 text-white text-xs rounded-md px-2 py-1.5 outline-none border border-emerald-500"
              >
                {automationAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.display_phone ? ` — ${a.display_phone}` : ''}{a.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!automationOnly && (
            <div className="mt-2">
              <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
                className="w-full bg-emerald-600 text-white text-sm rounded-lg px-3 py-2 outline-none border border-emerald-500">
                <option value="">All Users</option>
                <option value="__unassigned__">Unassigned</option>
                {assignedUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="animate-spin text-emerald-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-500 mt-10 text-sm px-4 space-y-2">
              {automationOnly ? (
                <>
                  <p className="font-medium text-gray-600">No chats for this account yet</p>
                  {activeAccount?.display_phone && (
                    <p className="text-xs">Number: <span className="font-mono text-gray-700">{activeAccount.display_phone}</span></p>
                  )}
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Incoming messages tab mein tabhi dikhenge jab Meta webhook se message aayega aur
                    <code className="mx-1 text-[10px] bg-gray-100 px-1 rounded">account_id</code>
                    is number se match ho. Dropdown se doosra account try karo (e.g. Default TC number).
                  </p>
                </>
              ) : (
                <p>No conversations</p>
              )}
            </div>
          ) : filtered.map(c => (
            <div key={c.id} onClick={() => openChat(c)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition ${selectedContact?.id === c.id ? 'bg-emerald-50' : ''}`}>
              <div className="w-11 h-11 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {(c.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <p className={`text-sm truncate ${c.unread_count > 0 ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                    {c.name || 'Unknown'}
                  </p>
                  <span className={`text-xs flex-shrink-0 ${c.unread_count > 0 ? 'text-emerald-600 font-semibold' : 'text-gray-400'}`}>
                    {fmtTime(c.last_message_at)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-0.5">
                  <p className="text-xs text-gray-400 truncate">
                    {maskPhone(c.phone)}
                    {c.assigned_to ? ` • ${getUserName(c.assigned_to)}` : ' • Unassigned'}
                  </p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {c.is_muted && <VolumeX size={12} className="text-gray-400" />}
                    {c.unread_count > 0 && (
                      <span className="bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Chat */}
      <div className={`${selectedContact ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-[#efeae2]`}>
        {!selectedContact ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MessageSquare size={64} className="mb-4 text-gray-300" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose from the contacts list to view messages</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3">
              <button onClick={() => setSelectedContact(null)} className="md:hidden p-1"><ChevronLeft size={20} /></button>
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-sm flex-shrink-0">
                {(selectedContact.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{selectedContact.name || 'Unknown'}</p>
                <p className="text-xs text-emerald-200 truncate">
                  {maskPhone(selectedContact.phone)} • {getUserName(selectedContact.assigned_to)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={toggleMute} className="p-2 hover:bg-emerald-600 rounded-lg transition" title={selectedContact.is_muted ? 'Unmute' : 'Mute'}>
                  {selectedContact.is_muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <button onClick={() => { setShowReassign(!showReassign); setShowTemplates(false); }}
                  className="p-2 hover:bg-emerald-600 rounded-lg transition" title="Reassign">
                  <UserCheck size={18} />
                </button>
                <button onClick={() => { syncTemplates(); setShowReassign(false); }}
                  className="p-2 hover:bg-emerald-600 rounded-lg transition" title="Templates">
                  <ArrowDownCircle size={18} />
                </button>
              </div>
            </div>

            {/* Reassign Panel */}
            {showReassign && (
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
                <Users size={16} className="text-gray-500" />
                <span className="text-sm text-gray-600 font-medium">Reassign to:</span>
                <select value={reassignUser} onChange={e => setReassignUser(e.target.value)}
                  className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  <option value="">Select user...</option>
                  {telecallers.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email} ({u.phone || '-'})</option>
                  ))}
                </select>
                <button onClick={handleReassign} disabled={!reassignUser}
                  className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-500 disabled:opacity-40 transition">
                  Assign
                </button>
                <button onClick={() => setShowReassign(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
            )}

            {/* Template Panel */}
            {showTemplates && (
              <div className="bg-white border-b border-gray-200 px-4 py-3 max-h-48 overflow-y-auto">
                <p className="text-sm font-semibold text-gray-700 mb-2">Approved Templates ({templates.length})</p>
                {templates.length === 0 ? (
                  <p className="text-xs text-gray-400">No approved templates found</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {templates.map((tpl, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-2 hover:bg-emerald-50 cursor-pointer transition"
                        onClick={() => sendTemplate(tpl)}>
                        <p className="text-sm font-medium text-gray-800">{tpl.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">{tpl.body_text}</p>
                        <span className="text-[10px] text-emerald-600 font-medium">{tpl.language} • Click to send</span>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => setShowTemplates(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Close</button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {messages.map((m) => {
                const isOut = m.direction === 'outbound';
                const displayContent = resolveMessageDisplayContent(m, templatesByName) || `[${m.message_type}]`;
                const templateButtons = resolveTemplateButtons(m, templatesByName);
                const replyPreview = m.reply_to_content
                  ? resolveReplyPreview(
                    m.reply_to_content,
                    templatesByName,
                    m.reply_to_wa_message_id ? messageByWaId[m.reply_to_wa_message_id] : null,
                  )
                  : null;
                return (
                  <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-lg shadow-sm overflow-hidden ${isOut ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
                      <div className="px-3 pt-2">
                        {replyPreview && (
                          <div className="border-l-4 border-emerald-400 bg-emerald-50 rounded px-2 py-1 mb-1 text-xs text-gray-600 line-clamp-2">
                            {replyPreview}
                          </div>
                        )}
                        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{displayContent}</p>
                      </div>
                      <TemplateBubbleButtons buttons={templateButtons} />
                      <div className="flex items-center justify-end gap-1 px-3 pb-2 pt-0.5">
                        <span className="text-[10px] text-gray-400">
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isOut && statusIcon(m.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3">
              <input value={msgText} onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Type a message..." className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition" />
              <button onClick={sendMessage} disabled={sending || !msgText.trim()}
                className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center hover:bg-emerald-500 disabled:opacity-40 transition">
                <Send size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
